import React, { createContext, useContext } from 'react';
import {
	fetchDataPreviewsQuery,
	useAxiosInstance,
	usePoQuery,
} from '@gov-nx/api';
import { useProcessControl } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { PageCode } from '../../definitions/codes';
import type {
	RegistrVozidelResponse,
	TechnicalInspectionResponse,
} from '../vehicle.types';
import {
	getPreviousKeepers,
	getPreviousOwners,
	getVehicleDetailData,
	getVehicleGetters,
} from '../vehicleUtils';
import { FormDefinitionInstance } from './FormDefinitions';
import type {
	RegisterSVDetailContext,
	RegisterSVDetailControl,
} from './context.types';
import { getVehicleDocumentsKeyValues } from './keyValues';

const RegistrSVContext = createContext<Nullable<RegisterSVDetailContext>>(null);

interface RegistrSVDetailContextProviderProps {
	code: PageCode;
	vehicleId: number;
	ico?: number;
	children: React.ReactNode;
}

export function RegistrSVDetailContextProvider({
	code,
	children,
	vehicleId,
	ico,
}: RegistrSVDetailContextProviderProps) {
	const { controls, setControls } = useProcessControl<RegisterSVDetailControl>({
		isTechnicalInspectionInfoLoading: true,
	});
	const { t } = useLocale(code);
	const formDefinition = FormDefinitionInstance();
	const { axiosInstance } = useAxiosInstance();

	const query = usePoQuery(
		{
			queryKey: ['nahledyudaju', 'zdrojUdaju', 'REGISTR_VOZIDEL', vehicleId],
			refetchOnWindowFocus: false,
			queryFn: async () =>
				fetchDataPreviewsQuery(axiosInstance)<
					{ zdrojUdaju: string; vozidloID: number; ico?: number },
					RegistrVozidelResponse
				>({
					zdrojUdaju: 'REGISTR_VOZIDEL_V2',
					vozidloID: vehicleId,
					ico,
				}),
			onError: (initialError) => {
				setControls({ initialLoading: false, initialError });
			},
		},
		{
			doNotShowToastError: true,
		}
	);

	const getOtherRecords = () => {
		const vozidloUdaje = vehicle?.vozidloUdaje;
		if (!vozidloUdaje) return;

		return vozidloUdaje.udajeDodatek?.dalsiZaznamy?.xVal
			?.split('|')
			.map((valueSegment) => valueSegment.trim());
	};

	const getRelationshipsToTheVehicle = () => {
		const subjekty = vehicle?.subjekty;
		if (!subjekty) return [];
		const subjektArray = Array.isArray(subjekty.subjekt)
			? subjekty.subjekt
			: [subjekty.subjekt];
		const aktualniSubjekty = subjektArray.filter(
			(subjekt) => subjekt.subjektAktualni?.xVal === '1'
		);

		return aktualniSubjekty.map((aktualni) => ({
			vztah:
				aktualni?.subjektTyp.xVal === '1'
					? 'formular.pole.filter-vztah-vlastnik'
					: 'formular.pole.filter-vztah-provozovatel',
			od: aktualni?.subjektOd?.xVal,
		}));
	};

	const vehicle = getVehicleDetailData(query.data);
	const vinNumber = vehicle?.vozidloUdaje.udajeZakladni?.vozidloVin?.xVal;

	const technicalInspectionInfoQuery = usePoQuery({
		queryKey: ['technical-inspection', vinNumber],
		queryFn: async () => {
			setControls({ isTechnicalInspectionInfoLoading: true });
			return fetchDataPreviewsQuery(axiosInstance)<
				{ zdrojUdaju: string; vin?: string },
				TechnicalInspectionResponse
			>({
				zdrojUdaju: 'TECHNICKE_PROHLIDKY_ODKAZ',
				vin: vinNumber,
			});
		},
		onSuccess: () => {
			setControls({ isTechnicalInspectionInfoLoading: false });
		},
		onError: (initialError) => {
			setControls({ isTechnicalInspectionInfoLoading: false, initialError });
		},
		enabled: !!vinNumber,
		refetchOnWindowFocus: false,
	});

	return (
		<RegistrSVContext.Provider
			value={{
				isLoading: query.isLoading,
				code,
				controls,
				setControls,
				formDefinition,
				vehicle,
				technicalInspectionURL:
					technicalInspectionInfoQuery.data?.data.Data?.[0].odkaz,
				vehicleGetters: getVehicleGetters,
				otherRecords: getOtherRecords(),
				relationshipsToTheVehicle: getRelationshipsToTheVehicle(),
				previousOwners: getPreviousOwners(vehicle),
				previousKeepers: getPreviousKeepers(vehicle),
				...getVehicleDocumentsKeyValues(vehicle, t),
			}}>
			{children}
		</RegistrSVContext.Provider>
	);
}

export const useRegistrSVDetailContext = (): RegisterSVDetailContext =>
	useContext(RegistrSVContext) as RegisterSVDetailContext;
